import { normalizeSeriesTypeId } from "./types/pane-descriptor";
import { PRIMARY_BASELINE_KEY } from "@vninvestcharts/engine-core";
function numberParam(series, key, fallback) {
    const value = series.params?.[key];
    return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function asNumber(value, pick) {
    if (value === undefined) {
        return undefined;
    }
    if (typeof value === "number") {
        return value;
    }
    return pick?.(value);
}
/**
 * Collapses a datum value to the single number a scalar consumer (tooltip, legend, Y-domain)
 * can display. A multi-plot Pine script yields a keyed object per bar; we take the first
 * declared plot, matching what `computeIndicatorComputationResult` puts on the wire, so the
 * widget and the engine never disagree about which line they are looking at.
 */
function asScalar(value) {
    if (value === undefined || value === null)
        return undefined;
    if (typeof value === "number")
        return Number.isFinite(value) ? value : undefined;
    if (typeof value === "object" && !Array.isArray(value)) {
        const first = Object.values(value)[0];
        return typeof first === "number" && Number.isFinite(first) ? first : undefined;
    }
    return undefined;
}
/**
 * The Pine series-type prefix, and the only place that parses it.
 *
 * A Pine script produces a series whose type id is generated at runtime, so it is absent from
 * every closed lookup table in this file. Both the producer (PineEditorPanel) and the consumer
 * (buildIndicatorBaseKey) must agree on the format; when they disagreed, the series resolved to
 * no key and vanished. One exported pair, one format, no second spelling.
 *
 * `scriptId` is the persisted script id (base36 + timestamp), which never contains a dot — that
 * matters, because the engine treats a type containing "." as a derived sub-series.
 */
export const PINE_SERIES_TYPE_PREFIX = "pine_";
/**
 * Idempotent on purpose: `useScriptPersistence.generateId()` already emits ids beginning with
 * `pine_`, so the script id and the series type are the same string in practice. Making this
 * idempotent means a caller can pass either form without producing `pine_pine_...`.
 */
export function pineSeriesTypeFor(scriptId) {
    return scriptId.startsWith(PINE_SERIES_TYPE_PREFIX)
        ? scriptId
        : `${PINE_SERIES_TYPE_PREFIX}${scriptId}`;
}
/** Returns the script id, or null when the type is not a Pine series type. */
export function pineScriptIdFromSeriesType(type) {
    if (!type.startsWith(PINE_SERIES_TYPE_PREFIX))
        return null;
    const scriptId = type.slice(PINE_SERIES_TYPE_PREFIX.length);
    // Guard the engine's derived-sub-series rule and reject an empty suffix.
    if (scriptId.length === 0 || scriptId.includes("."))
        return null;
    return scriptId;
}
function buildIndicatorBaseKey(series) {
    switch (normalizeSeriesTypeId(series.type)) {
        case "EMA":
            return `EMA:period=${numberParam(series, "period", 20)}`;
        case "MA":
            return `MA:period=${numberParam(series, "period", 20)}`;
        case "BollingerBand":
            return `BollingerBand:period=${numberParam(series, "period", 20)}:stdDev=${numberParam(series, "stdDev", 2)}`;
        case "RSI":
            return `RSI:period=${numberParam(series, "period", 14)}`;
        case "MACD":
            return `MACD:fast=${numberParam(series, "fast", 12)}:slow=${numberParam(series, "slow", 26)}:signal=${numberParam(series, "signal", 9)}`;
        case "Whale":
            return `Whale:threshold=${numberParam(series, "threshold", 50_000)}`;
        case "SAR":
            return `SAR:afStep=${numberParam(series, "afStep", 0.02)}:afMax=${numberParam(series, "afMax", 0.2)}`;
        case "WR":
            return `WR:period=${numberParam(series, "period", 14)}`;
        case "VR":
            return `VR:period=${numberParam(series, "period", 26)}`;
        case "KDJ":
            return `KDJ:period=${numberParam(series, "period", 9)}:m1=${numberParam(series, "m1", 3)}:m2=${numberParam(series, "m2", 3)}`;
        case "CCI":
            return `CCI:period=${numberParam(series, "period", 20)}`;
        case "DMI":
            return `DMI:period=${numberParam(series, "period", 14)}`;
        case "BIAS":
            return `BIAS:period=${numberParam(series, "period", 6)}`;
        case "BRAR":
            return `BRAR:period=${numberParam(series, "period", 26)}`;
        case "MTM":
            return `MTM:period=${numberParam(series, "period", 6)}:signalPeriod=${numberParam(series, "signalPeriod", 6)}`;
        case "EMV":
            return `EMV:period=${numberParam(series, "period", 14)}`;
        case "AO":
            return "AO";
        case "ROC":
            return `ROC:period=${numberParam(series, "period", 12)}`;
        case "TRIX":
            return `TRIX:period=${numberParam(series, "period", 12)}:signalPeriod=${numberParam(series, "signalPeriod", 9)}`;
        case "DMA":
            return `DMA:fastPeriod=${numberParam(series, "fastPeriod", 10)}:slowPeriod=${numberParam(series, "slowPeriod", 50)}:signalPeriod=${numberParam(series, "signalPeriod", 10)}`;
        case "PVT":
            return "PVT";
        case "PSY":
            return `PSY:period=${numberParam(series, "period", 12)}:signalPeriod=${numberParam(series, "signalPeriod", 6)}`;
        case "CR":
            return `CR:period=${numberParam(series, "period", 26)}:m1=${numberParam(series, "m1", 10)}:m2=${numberParam(series, "m2", 20)}:m3=${numberParam(series, "m3", 40)}:m4=${numberParam(series, "m4", 60)}`;
        // CE26 additions
        case "WMA":
            return `WMA:period=${numberParam(series, "period", 9)}`;
        case "VWMA":
            return `VWMA:period=${numberParam(series, "period", 20)}`;
        case "HMA":
            return `HMA:period=${numberParam(series, "period", 9)}`;
        case "DEMA":
            return `DEMA:period=${numberParam(series, "period", 9)}`;
        case "TEMA":
            return `TEMA:period=${numberParam(series, "period", 9)}`;
        case "MARibbon": {
            const type = series.params?.type || "EMA";
            const periods = series.params?.periods || [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
            return `MARibbon:type=${type}:periods=${periods.join(",")}`;
        }
        case "Ichimoku":
            return `Ichimoku:tenkan=${numberParam(series, "tenkan", 9)}:kijun=${numberParam(series, "kijun", 26)}:senkouB=${numberParam(series, "senkouB", 52)}:displacement=${numberParam(series, "displacement", 26)}`;
        case "Supertrend":
            return `Supertrend:period=${numberParam(series, "period", 10)}:multiplier=${numberParam(series, "multiplier", 3)}`;
        case "Keltner":
            return `Keltner:period=${numberParam(series, "period", 20)}:atrPeriod=${numberParam(series, "atrPeriod", 10)}:multiplier=${numberParam(series, "multiplier", 2)}`;
        case "Donchian":
            return `Donchian:period=${numberParam(series, "period", 20)}`;
        case "ChaikinVol":
            return `ChaikinVol:period=${numberParam(series, "period", 10)}`;
        case "StdDev":
            return `StdDev:period=${numberParam(series, "period", 20)}`;
        case "HV": {
            const annualize = series.params?.annualize !== false;
            return `HV:period=${numberParam(series, "period", 20)}:annualize=${annualize}`;
        }
        case "CI":
            return `CI:period=${numberParam(series, "period", 14)}`;
        case "CVDApprox":
            return "CVDApprox";
        case "CVDRealtime":
            return "CVDRealtime";
        case "StrengthElder":
            return "StrengthElder";
        case "StrengthRelative": {
            const compareSymbol = series.params?.compareSymbol || "VNINDEX";
            const period = numberParam(series, "period", 14);
            return `StrengthRelative:compareSymbol=${compareSymbol}:period=${period}`;
        }
        case "SMFI":
            return "SMFI";
        case "WyckoffWave": {
            const thresholdPercent = numberParam(series, "thresholdPercent", 1);
            return `WyckoffWave:thresholdPercent=${thresholdPercent}`;
        }
        case "BBI":
            return "BBI";
        case "OBV":
            return "OBV";
        case "CMF":
            return `CMF:period=${numberParam(series, "period", 20)}`;
        case "MFI":
            return `MFI:period=${numberParam(series, "period", 14)}`;
        case "VolOsc":
            return `VolOsc:shortPeriod=${numberParam(series, "shortPeriod", 5)}:longPeriod=${numberParam(series, "longPeriod", 20)}`;
        case "ChaikinOscillator":
            return `ChaikinOscillator:fast=${numberParam(series, "fast", 3)}:slow=${numberParam(series, "slow", 10)}`;
        case "VWAP": {
            const anchor = series.params?.anchor || "session";
            const showBands = series.params?.showBands !== false;
            const multiplier = numberParam(series, "multiplier", 1);
            return `VWAP:anchor=${anchor}:showBands=${showBands}:multiplier=${multiplier}`;
        }
        case "AnchoredVWAP": {
            const anchorTime = numberParam(series, "anchorTime", 0);
            const showBands = series.params?.showBands !== false;
            const multiplier = numberParam(series, "multiplier", 1);
            return `AnchoredVWAP:anchorTime=${anchorTime}:showBands=${showBands}:multiplier=${multiplier}`;
        }
        case "Alligator": {
            const jawPeriod = numberParam(series, "jawPeriod", 13);
            const jawShift = numberParam(series, "jawShift", 8);
            const teethPeriod = numberParam(series, "teethPeriod", 8);
            const teethShift = numberParam(series, "teethShift", 5);
            const lipsPeriod = numberParam(series, "lipsPeriod", 5);
            const lipsShift = numberParam(series, "lipsShift", 3);
            return `Alligator:jawPeriod=${jawPeriod}:jawShift=${jawShift}:teethPeriod=${teethPeriod}:teethShift=${teethShift}:lipsPeriod=${lipsPeriod}:lipsShift=${lipsShift}`;
        }
        case "Fractals":
            return "Fractals";
        case "Gator": {
            const jawPeriod = numberParam(series, "jawPeriod", 13);
            const jawShift = numberParam(series, "jawShift", 8);
            const teethPeriod = numberParam(series, "teethPeriod", 8);
            const teethShift = numberParam(series, "teethShift", 5);
            const lipsPeriod = numberParam(series, "lipsPeriod", 5);
            const lipsShift = numberParam(series, "lipsShift", 3);
            return `Gator:jawPeriod=${jawPeriod}:jawShift=${jawShift}:teethPeriod=${teethPeriod}:teethShift=${teethShift}:lipsPeriod=${lipsPeriod}:lipsShift=${lipsShift}`;
        }
        case "AC":
            return "AC";
        case "PivotStandard":
            return `PivotStandard:pivotType=${numberParam(series, "pivotType", 0)}`;
        case "PivotHighLow": {
            const lookback = numberParam(series, "lookback", 10);
            const pivotType = numberParam(series, "pivotType", 0);
            return `PivotHighLow:lookback=${lookback}:pivotType=${pivotType}`;
        }
        case "MarketProfile": {
            const tickSize = numberParam(series, "tickSize", 0.01);
            const tpoPeriodMinutes = numberParam(series, "tpoPeriodMinutes", 30);
            return `MarketProfile:tickSize=${tickSize}:tpoPeriodMinutes=${tpoPeriodMinutes}`;
        }
        case "VolumeProfile": {
            const priceTickSize = numberParam(series, "priceTickSize", 0.01);
            return `VolumeProfile:priceTickSize=${priceTickSize}`;
        }
        case "Footprint": {
            const barIntervalMs = numberParam(series, "barIntervalMs", 60000);
            const priceTickSize = numberParam(series, "priceTickSize", 0.01);
            return `Footprint:barIntervalMs=${barIntervalMs}:priceTickSize=${priceTickSize}`;
        }
        case "DeltaProfile": {
            const barIntervalMs = numberParam(series, "barIntervalMs", 60000);
            const priceTickSize = numberParam(series, "priceTickSize", 0.01);
            const smaPeriod = numberParam(series, "smaPeriod", 14);
            return `DeltaProfile:barIntervalMs=${barIntervalMs}:priceTickSize=${priceTickSize}:smaPeriod=${smaPeriod}`;
        }
        default: {
            // G-PINE-KEY: Pine registers a series under a type id that does not exist at compile
            // time (`pine_<scriptId>`), so it can never appear in the switch above. Without this
            // branch the default returned undefined, `computeIndicatorComputationResult` hit its
            // `if (!key) continue` guard BEFORE it reached `getPinePlugin`, and a script that
            // compiled cleanly rendered nothing — no error anywhere.
            //
            // The key is structured, not the raw type, so two scripts can never collide, and the
            // prefix makes dynamic keys identifiable downstream instead of looking like a typo.
            const scriptId = pineScriptIdFromSeriesType(series.type);
            if (scriptId)
                return `PINE:${scriptId}`;
            return undefined;
        }
    }
}
export function buildIndicatorSeriesKey(series) {
    const key = buildIndicatorBaseKey(series);
    if (!key) {
        return undefined;
    }
    let suffix = "";
    if (series.sourceType === "indicator" && series.sourceId) {
        suffix = `:sourceType=indicator:sourceId=${series.sourceId}:sourceKey=${series.sourceKey ?? "close"}`;
    }
    else if (series.sourceKey && series.sourceKey !== "close") {
        suffix = `:sourceKey=${series.sourceKey}`;
    }
    if (series.params?.timeframe && series.params.timeframe !== "Same as chart") {
        suffix += `:timeframe=${series.params.timeframe}`;
    }
    return key + suffix;
}
export function resolveSeriesDatumValue(datum, series) {
    const key = buildIndicatorSeriesKey(series);
    if (key) {
        return datum.indicatorValues?.[key];
    }
    return undefined;
}
/**
 * Cạnh dẫn đầu của cột Point & Figure tại bar này — `close` của box CUỐI.
 *
 * Vì sao phải là box cuối chứ không phải `boxes[0]`: một bar có thể tạo nhiều box
 * (gap lớn), và box đầu tiên là mép DƯỚI của phần vừa hình thành, tức là đáy cột
 * chứ không phải giá hiện tại. Đọc `boxes[0]` sẽ báo giá thấp hơn thực tế tới
 * cả một bậc.
 *
 * Vì sao đây chỉ là MỘT số: đó là giới hạn của giao diện `resolveSeriesValue` —
 * mỗi bar một giá trị, và mỗi rect là một giá trị. Phần hình học còn lại của cột
 * (thang box) do `layoutPointAndFigure` của engine dựng, và miền giá được nới
 * bằng `widenDomainForPointAndFigure` — xem `exoticSeriesModes.isColumnStackMode`.
 */
function resolvePointAndFigureEdge(datum) {
    const boxes = datum.boxes;
    if (!boxes || boxes.length === 0) {
        return undefined;
    }
    return boxes[boxes.length - 1]?.close;
}
function resolveOverlayValue(datum, series) {
    if (!series.overlaySymbolId) {
        return undefined;
    }
    if (series.normalizeToBaseline === true) {
        const np = datum.normalizedPrices?.[series.overlaySymbolId];
        if (typeof np === "number") {
            return np;
        }
    }
    return datum.symbolData?.[series.overlaySymbolId]?.close;
}
export function resolveSeriesValue(datum, series) {
    const overlayVal = resolveOverlayValue(datum, series);
    if (overlayVal !== undefined) {
        return overlayVal;
    }
    // Pine: a runtime-generated type id that this closed `switch` cannot name, so it would fall
    // through to `default: return datum.close` and the tooltip/legend/Y-domain would all report
    // the bar's close instead of the script's value. That is a silent wrong answer that looks
    // plausible, so it is handled BEFORE the switch rather than as another case.
    // `buildIndicatorSeriesKey` already resolves this type to the structured `PINE:<scriptId>`
    // key (see `buildIndicatorBaseKey`'s default branch), which is the key `enrichData` writes.
    const pineScriptId = pineScriptIdFromSeriesType(series.type);
    if (pineScriptId) {
        return asScalar(resolveSeriesDatumValue(datum, series));
    }
    switch (normalizeSeriesTypeId(series.type)) {
        case "Candlestick":
        case "HollowCandle":
        case "OHLC":
        case "HeikinAshi":
        case "Line":
        case "Area":
            return datum.close;
        case "Volume":
            return datum.volume;
        case "EMA":
        case "MA":
        case "RSI":
        case "CVDApprox":
        case "CVDRealtime":
        case "StrengthRelative":
        case "SMFI":
        case "CCI":
        case "BIAS":
        case "AO":
        case "ROC":
        case "PVT":
        case "WR":
        case "VR":
        case "WMA": // CE26 additions
        case "VWMA":
        case "HMA":
        case "DEMA":
        case "TEMA":
        case "ChaikinVol":
        case "StdDev":
        case "HV":
        case "CI":
            return asNumber(resolveSeriesDatumValue(datum, series));
        case "KDJ":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.j);
        case "DMI":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.adx);
        case "BBI":
            return asNumber(resolveSeriesDatumValue(datum, series));
        case "SAR":
            return asNumber(resolveSeriesDatumValue(datum, series));
        case "OBV":
            return asNumber(resolveSeriesDatumValue(datum, series));
        case "BRAR":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.br);
        case "MTM":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.mtm);
        case "EMV":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.emv);
        case "MACD":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.macd);
        case "BollingerBand":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.middle);
        case "Whale":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => {
                const whale = value;
                return whale.whaleBuyVol ?? whale.whaleSellVol;
            });
        case "TRIX":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.trix);
        case "DMA":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.ddd);
        case "PSY":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.psy);
        case "CR":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.cr);
        case "StrengthElder":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.bullPower);
        // CE26 additions
        case "MARibbon":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => Object.values(value)[0]);
        case "Ichimoku":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.tenkan);
        case "Supertrend":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.supertrend);
        case "Keltner":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.middle);
        case "Donchian":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.middle);
        case "CMF":
        case "MFI":
        case "VolOsc":
        case "ChaikinOscillator":
            return asNumber(resolveSeriesDatumValue(datum, series));
        case "AC":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.ac);
        case "VWAP":
        case "AnchoredVWAP":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.vwap);
        case "Alligator":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.jaw);
        case "Fractals":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.bearish);
        case "Gator":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.upper);
        case "PivotStandard":
        case "PivotHighLow":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.pp);
        case "MarketProfile":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.poc);
        case "VolumeProfile":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.poc);
        case "Footprint":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.close);
        case "DeltaProfile":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.cumulativeDelta);
        case "WyckoffWave":
            return asNumber(resolveSeriesDatumValue(datum, series), (value) => value.zigzag);
        case "Comparison":
            return undefined;
        // HTF overlay: giá trị là NẾN KHUNG CAO HƠN, không phải close của khung
        // hiện tại. Thiếu case này → `default` trả `datum.close` (khung LTF) và
        // tooltip/legend/extent đọc nhầm giá trị (registry vẫn đang cast `any`).
        case "HTFOverlay":
            return datum.htf?.close;
        // ── A3 Baseline / A11 Renko / Kagi / Point & Figure / Line Break ─────────
        // Đọc DỮ LIỆU ĐÃ SẢN XUẤT, không đọc `close`. Thiếu case ở đây là nguyên
        // nhân gốc của "picker hứa nhưng vẽ y hệt Line": `default` trả về
        // `datum.close` nên chart loại vẽ đúng thứ nó vẽ với nhãn khác.
        // Trả `undefined` (không vẽ) khi producer chưa chạy — fail-closed, thà
        // không vẽ còn hơn vẽ một đường giả.
        case "Baseline":
            return datum.normalizedPrices?.[PRIMARY_BASELINE_KEY];
        case "Renko":
            return datum.renkoClose;
        case "Kagi":
            return datum.current;
        case "PointAndFigure":
            return resolvePointAndFigureEdge(datum);
        case "LineBreak":
            return datum.lineBreakClose;
        default:
            return datum.close;
    }
}
export function resolveSeriesStructuredValue(datum, series) {
    switch (normalizeSeriesTypeId(series.type)) {
        case "BollingerBand":
            return resolveSeriesDatumValue(datum, series);
        case "MACD":
            return resolveSeriesDatumValue(datum, series);
        case "Whale":
            return resolveSeriesDatumValue(datum, series);
        case "KDJ":
            return resolveSeriesDatumValue(datum, series);
        case "DMI":
            return resolveSeriesDatumValue(datum, series);
        case "BRAR":
            return resolveSeriesDatumValue(datum, series);
        case "MTM":
            return resolveSeriesDatumValue(datum, series);
        case "EMV":
            return resolveSeriesDatumValue(datum, series);
        case "TRIX":
            return resolveSeriesDatumValue(datum, series);
        case "DMA":
            return resolveSeriesDatumValue(datum, series);
        case "PSY":
            return resolveSeriesDatumValue(datum, series);
        case "CR":
            return resolveSeriesDatumValue(datum, series);
        case "StrengthElder":
            return resolveSeriesDatumValue(datum, series);
        // CE26 additions
        case "MARibbon":
            return resolveSeriesDatumValue(datum, series);
        case "Ichimoku":
            return resolveSeriesDatumValue(datum, series);
        case "Supertrend":
            return resolveSeriesDatumValue(datum, series);
        case "Keltner":
            return resolveSeriesDatumValue(datum, series);
        case "Donchian":
            return resolveSeriesDatumValue(datum, series);
        case "VWAP":
        case "AnchoredVWAP":
            return resolveSeriesDatumValue(datum, series);
        case "Alligator":
            return resolveSeriesDatumValue(datum, series);
        case "Fractals":
            return resolveSeriesDatumValue(datum, series);
        case "Gator":
            return resolveSeriesDatumValue(datum, series);
        case "PivotStandard":
        case "PivotHighLow":
            return resolveSeriesDatumValue(datum, series);
        case "MarketProfile":
            return resolveSeriesDatumValue(datum, series);
        case "VolumeProfile":
            return resolveSeriesDatumValue(datum, series);
        case "Footprint":
            return resolveSeriesDatumValue(datum, series);
        case "DeltaProfile":
            return resolveSeriesDatumValue(datum, series);
        case "WyckoffWave":
            return resolveSeriesDatumValue(datum, series);
        default:
            return resolveSeriesDatumValue(datum, series);
    }
}
export function resolveSeriesValueAccessors(series) {
    switch (normalizeSeriesTypeId(series.type)) {
        case "Candlestick":
        case "HollowCandle":
        case "OHLC":
        case "HeikinAshi":
            if (series.overlaySymbolId) {
                return [
                    (datum) => datum.symbolData?.[series.overlaySymbolId]?.low ?? resolveOverlayValue(datum, series) ?? datum.low,
                    (datum) => datum.symbolData?.[series.overlaySymbolId]?.high ?? resolveOverlayValue(datum, series) ?? datum.high,
                ];
            }
            return [(datum) => datum.low, (datum) => datum.high];
        case "Volume":
            return [(datum) => datum.volume];
        case "EMA":
        case "MA":
        case "RSI":
        case "CVDApprox":
        case "CVDRealtime":
        case "StrengthRelative":
        case "SMFI":
        case "Line":
        case "Area":
        case "Baseline":
        case "Renko":
        case "Kagi":
        case "PointAndFigure":
        case "LineBreak":
        case "CCI":
        case "BIAS":
        case "AO":
        case "ROC":
        case "PVT":
            return [(datum) => resolveSeriesValue(datum, series)];
        case "KDJ":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.k,
                (datum) => resolveSeriesStructuredValue(datum, series)?.d,
                (datum) => resolveSeriesStructuredValue(datum, series)?.j,
            ];
        case "DMI":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.plusDI,
                (datum) => resolveSeriesStructuredValue(datum, series)?.minusDI,
                (datum) => resolveSeriesStructuredValue(datum, series)?.adx,
            ];
        case "BRAR":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.ar,
                (datum) => resolveSeriesStructuredValue(datum, series)?.br,
            ];
        case "MTM":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.mtm,
                (datum) => resolveSeriesStructuredValue(datum, series)?.signal,
            ];
        case "EMV":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.emv,
                (datum) => resolveSeriesStructuredValue(datum, series)?.signal,
            ];
        case "BollingerBand":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.top,
                (datum) => resolveSeriesStructuredValue(datum, series)?.middle,
                (datum) => resolveSeriesStructuredValue(datum, series)?.bottom,
            ];
        case "MACD":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.macd,
                (datum) => resolveSeriesStructuredValue(datum, series)?.signal,
                (datum) => resolveSeriesStructuredValue(datum, series)?.divergence,
            ];
        case "TRIX":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.trix,
                (datum) => resolveSeriesStructuredValue(datum, series)?.signal,
            ];
        case "DMA":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.ddd,
                (datum) => resolveSeriesStructuredValue(datum, series)?.ama,
            ];
        case "PSY":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.psy,
                (datum) => resolveSeriesStructuredValue(datum, series)?.signal,
            ];
        case "CR":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.cr,
                (datum) => resolveSeriesStructuredValue(datum, series)?.ma1,
                (datum) => resolveSeriesStructuredValue(datum, series)?.ma2,
                (datum) => resolveSeriesStructuredValue(datum, series)?.ma3,
                (datum) => resolveSeriesStructuredValue(datum, series)?.ma4,
            ];
        case "StrengthElder":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.bullPower,
                (datum) => resolveSeriesStructuredValue(datum, series)?.bearPower,
            ];
        case "Whale":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.whaleBuyVol,
                (datum) => resolveSeriesStructuredValue(datum, series)?.whaleSellVol,
            ];
        // CE26 additions
        case "MARibbon": {
            const periods = series.params?.periods || [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
            return periods.map(p => (datum) => resolveSeriesStructuredValue(datum, series)?.[`ma${p}`]);
        }
        case "Ichimoku":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.tenkan,
                (datum) => resolveSeriesStructuredValue(datum, series)?.kijun,
                (datum) => resolveSeriesStructuredValue(datum, series)?.senkouA,
                (datum) => resolveSeriesStructuredValue(datum, series)?.senkouB,
                (datum) => resolveSeriesStructuredValue(datum, series)?.chikou,
            ];
        case "Supertrend":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.supertrend,
            ];
        case "Keltner":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.upper,
                (datum) => resolveSeriesStructuredValue(datum, series)?.middle,
                (datum) => resolveSeriesStructuredValue(datum, series)?.lower,
            ];
        case "Donchian":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.upper,
                (datum) => resolveSeriesStructuredValue(datum, series)?.middle,
                (datum) => resolveSeriesStructuredValue(datum, series)?.lower,
            ];
        case "CMF":
        case "MFI":
        case "VolOsc":
        case "AC":
            return [(datum) => resolveSeriesValue(datum, series)];
        case "VWAP":
        case "AnchoredVWAP":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.vwap,
                (datum) => resolveSeriesStructuredValue(datum, series)?.upper1,
                (datum) => resolveSeriesStructuredValue(datum, series)?.lower1,
                (datum) => resolveSeriesStructuredValue(datum, series)?.upper2,
                (datum) => resolveSeriesStructuredValue(datum, series)?.lower2,
                (datum) => resolveSeriesStructuredValue(datum, series)?.upper3,
                (datum) => resolveSeriesStructuredValue(datum, series)?.lower3,
            ];
        case "Alligator":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.jaw,
                (datum) => resolveSeriesStructuredValue(datum, series)?.teeth,
                (datum) => resolveSeriesStructuredValue(datum, series)?.lips,
            ];
        case "Fractals":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.bearish,
                (datum) => resolveSeriesStructuredValue(datum, series)?.bullish,
            ];
        case "Gator":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.upper,
                (datum) => resolveSeriesStructuredValue(datum, series)?.lower,
            ];
        case "PivotStandard":
        case "PivotHighLow":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.pp,
                (datum) => resolveSeriesStructuredValue(datum, series)?.r1,
                (datum) => resolveSeriesStructuredValue(datum, series)?.s1,
                (datum) => resolveSeriesStructuredValue(datum, series)?.r2,
                (datum) => resolveSeriesStructuredValue(datum, series)?.s2,
                (datum) => resolveSeriesStructuredValue(datum, series)?.r3,
                (datum) => resolveSeriesStructuredValue(datum, series)?.s3,
            ];
        case "MarketProfile":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.poc,
                (datum) => resolveSeriesStructuredValue(datum, series)?.valueAreaHigh,
                (datum) => resolveSeriesStructuredValue(datum, series)?.valueAreaLow,
            ];
        case "VolumeProfile":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.poc,
                (datum) => resolveSeriesStructuredValue(datum, series)?.valueAreaHigh,
                (datum) => resolveSeriesStructuredValue(datum, series)?.valueAreaLow,
            ];
        case "Footprint":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.high,
                (datum) => resolveSeriesStructuredValue(datum, series)?.low,
            ];
        case "DeltaProfile":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.cumulativeDelta,
                (datum) => resolveSeriesStructuredValue(datum, series)?.barDelta,
                (datum) => resolveSeriesStructuredValue(datum, series)?.deltaSMA,
            ];
        case "WyckoffWave":
            return [
                (datum) => resolveSeriesStructuredValue(datum, series)?.zigzag,
            ];
        default:
            return [(datum) => resolveSeriesValue(datum, series)];
    }
}
